import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { CustomerService } from 'src/app/services/customer.service';
import { logError } from 'src/app/services/helpers/sentry.helper';
import { Country, otherCountries, prioCountries } from 'src/app/services/kingfisher/countries';
import { CreateCustomer } from 'src/app/services/kingfisher/create-customer';
import { Customer, HonorificPrefix } from 'src/app/services/kingfisher/customer';
import { ModalService } from 'src/app/services/modal.service';

/** Mobiles français, ou format international. Aligné sur celui du parcours d'inscription. */
const PHONE_PATTERN = /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/;

/** France uniquement : les autres pays n'ont pas de format commun exploitable. */
const POSTAL_CODE_PATTERN_FR = /^(?:0[1-9]|[1-8]\d|9[0-8])\d{3}$/;

type CivilityOption = {
  name: string;
  value: HonorificPrefix;
};

/**
 * Popup de complétion de profil.
 *
 * AuthApp ne collecte que les credentials : un client qui revient de CIAM a une fiche GCI sans
 * civilité, nom, prénom, téléphone ni adresse. Cette modale les réclame et les envoie en PATCH,
 * remplaçant le formulaire d'inscription qui les collectait *avant* la redirection.
 *
 * Elle est volontairement bloquante : aucune croix, aucun bouton d'annulation. `CustomerService`
 * la réarme à chaque obtention de session tant que le nom ou le prénom manque, donc un
 * rechargement de page ne permet pas de l'esquiver.
 */
@Component({
  selector: 'ng-casto-ciam-profile-completion',
  templateUrl: './ciam-profile-completion.component.html',
})
export class CiamProfileCompletionComponent implements OnInit {
  public form: FormGroup;
  public isLoading = false;
  public globalError = '';

  protected countries: Country[] = [...prioCountries, ...otherCountries];
  protected civilityValues: CivilityOption[] = [
    { name: this.translate.instant('customer-info-form.civility-form.m'), value: HonorificPrefix.Monsieur },
    { name: this.translate.instant('customer-info-form.civility-form.mme'), value: HonorificPrefix.Madame },
    { name: this.translate.instant('customer-info-form.civility-form.mmle'), value: HonorificPrefix.Mademoiselle },
  ];

  constructor(
    public readonly modalService: ModalService,
    private readonly customer: CustomerService,
    private readonly formBuilder: FormBuilder,
    private readonly translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.form = this.buildForm(this.modalService.currentDatas?.customer ?? this.customer.userDatas);
    this.applyCountryValidators(this.form.get('country').value);
    this.form.get('country').valueChanges.subscribe(country => this.applyCountryValidators(country));
  }

  hasError(controlName: string): boolean {
    const control = this.form.get(controlName);
    return control.invalid && (control.dirty || control.touched);
  }

  async submit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.globalError = '';
    this.isLoading = true;

    try {
      await this.customer.completeCurrentCustomerProfile(this.buildUpdate());
      this.modalService.close();
    } catch (err) {
      // La modale reste ouverte : la fiche est toujours incomplète, le client doit pouvoir
      // réessayer sans ressaisir. Un échec GCI ne doit pas le laisser passer.
      logError(err);
      this.globalError = this.translate.instant('popins.profile-completion.error');
    } finally {
      this.isLoading = false;
    }
  }

  private buildForm(customer: Customer | null): FormGroup {
    return this.formBuilder.group({
      honorificPrefix: [customer?.honorificPrefix ?? null, [Validators.required]],
      lastName: [customer?.lastname ?? '', [Validators.required]],
      firstName: [customer?.firstname ?? '', [Validators.required]],
      phoneNumber: [customer?.phoneNumber ?? '', [Validators.required, Validators.pattern(PHONE_PATTERN)]],
      addressLine1: [customer?.address?.line1 ?? '', [Validators.required]],
      // Seul champ optionnel : un pavillon n'a ni étage ni boîte aux lettres.
      addressComplement: [customer?.address?.complement ?? ''],
      postalCode: [customer?.address?.postalCode ?? '', [Validators.required]],
      city: [customer?.address?.city ?? '', [Validators.required]],
      country: [customer?.address?.country || 'FR', [Validators.required]],
    });
  }

  /** Le motif de code postal n'a de sens qu'en France : ailleurs, seule la présence compte. */
  private applyCountryValidators(country: string): void {
    const postalCode = this.form.get('postalCode');

    postalCode.setValidators(
      country === 'FR' ? [Validators.required, Validators.pattern(POSTAL_CODE_PATTERN_FR)] : [Validators.required]
    );
    postalCode.updateValueAndValidity({ emitEvent: false });
  }

  private buildUpdate(): CreateCustomer {
    return {
      // L'e-mail est l'identifiant CIAM du client : il n'est jamais patché ici. Vide pour que
      // `toCiamCustomerUpdate` l'omette du corps envoyé à GCI.
      email: '',
      honorificPrefix: this.form.get('honorificPrefix').value,
      familyName: this.form.get('lastName').value.trim(),
      givenName: this.form.get('firstName').value.trim(),
      phoneNumber: this.form.get('phoneNumber').value.trim(),
      address: {
        line1: this.form.get('addressLine1').value.trim(),
        complement: this.form.get('addressComplement').value.trim(),
        postalCode: this.form.get('postalCode').value.trim(),
        city: this.form.get('city').value.trim(),
        country: this.form.get('country').value,
      },
    } as CreateCustomer;
  }
}
